/**
 * ReversalModal — Modal de reversão de baixa inline (chamado da lista de títulos).
 */
import { useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { registerReversal } from '../services/settlementApi';
import { ReversalReason } from '../constants/settlement';
import { toast } from 'react-toastify';

interface Props {
    titleDebt: any;
    onClose: () => void;
    onSuccess: () => void;
}

const formatCurrency = (value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value || '0') : value;
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const ReversalModal: React.FC<Props> = ({ titleDebt, onClose, onSuccess }) => {
    const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
    const [notes, setNotes] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const toggleReason = (reason: string) => {
        setSelectedReasons(prev =>
            prev.includes(reason) ? prev.filter(r => r !== reason) : [...prev, reason]
        );
    };

    const handleSubmit = async () => {
        if (selectedReasons.length === 0) {
            toast.error('Selecione ao menos um motivo');
            return;
        }
        setSubmitting(true);
        try {
            await registerReversal(titleDebt.title_debt_id ?? titleDebt.id, { reasons: selectedReasons, notes });
            toast.success('Reversão registrada com sucesso');
            onSuccess();
            onClose();
        } catch (e: any) {
            toast.error(`Erro: ${e.message}`);
        }
        setSubmitting(false);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Reverter Baixa</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                        <FaTimes size={18} />
                    </button>
                </div>

                <div className="mb-4 bg-gray-50 dark:bg-slate-700 p-3 rounded text-sm">
                    <p><span className="text-gray-500">Contrato:</span> <strong>{titleDebt.contract_number}</strong></p>
                    <p><span className="text-gray-500">Parcela:</span> <strong>{titleDebt.document_title || titleDebt.installment_number || '-'}</strong></p>
                    <p><span className="text-gray-500">Valor pago:</span> <strong className="text-green-600">{formatCurrency(titleDebt.paid_amount)}</strong></p>
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Motivo(s) da reversão <span className="text-red-500">*</span>
                    </label>
                    <div className="space-y-2">
                        {Object.values(ReversalReason).map(reason => (
                            <label key={reason} className="flex items-center gap-2 text-sm cursor-pointer">
                                <input type="checkbox" className="rounded"
                                    checked={selectedReasons.includes(reason)}
                                    onChange={() => toggleReason(reason)} />
                                <span className="text-gray-700 dark:text-gray-300">{reason}</span>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Observações</label>
                    <textarea className="w-full border dark:border-slate-600 rounded px-3 py-2 text-sm dark:bg-slate-700 dark:text-white" rows={2}
                        value={notes} onChange={e => setNotes(e.target.value)}
                        placeholder="Detalhes adicionais..." />
                </div>

                <div className="flex justify-end gap-2">
                    <button onClick={onClose}
                        className="px-4 py-2 text-sm border rounded hover:bg-gray-50 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-gray-300">
                        Cancelar
                    </button>
                    <button onClick={handleSubmit} disabled={submitting || selectedReasons.length === 0}
                        className="px-4 py-2 text-sm bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50">
                        {submitting ? 'Revertendo...' : 'Confirmar Reversão'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReversalModal;
